import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { appLabel } from './format'

// The interim fix for apps that send a blank app header (old caffeine
// builds): content names the app when the log is read, from the request's
// session or route, and flags what it still can't name. None of it is
// stored: the row's clientApp / clientVersion / clientPlatform stay what the
// request log recorded, and an inferred app is always shown muted and marked
// so it never reads as what the client sent.
//
// Content's fields (audit/requests and audit/requests/:requestID):
//   inferredClientApp, inferredClientVersion, inferredClientPlatform,
//   inferredFrom: 'session' | 'route' | 'header',
//   inferredFromRequestID: the other request the inference rests on,
//   unidentified: 'signed_in' (red) | 'anonymous' (muted), after inference.

const FROM_TEXT = {
  session: 'inferred from session',
  route: 'inferred from route',
  header: 'from its app header'
}

// The Origin filter (content's ?unidentified=).
export const ORIGIN_CHOICES = [
  { value: '', label: 'Any origin' },
  { value: 'any', label: 'Unidentified origin' },
  { value: 'signed_in', label: 'Unidentified, signed in' },
  { value: 'anonymous', label: 'No app (anonymous)' }
]

const inferredApp = r => [r.inferredClientApp, r.inferredClientVersion].filter(Boolean).join(' ')

// "EdXR Editor · inferred from session"; '' when nothing was inferred.
export const inferredText = r => {
  if (!r || !r.inferredClientApp) return ''
  return `${inferredApp(r)} · ${FROM_TEXT[r.inferredFrom] || 'inferred'}`
}

const storedAs = r => `Stored as: ${appLabel(r) || 'Unknown'}`

// The grid's App column.
export const AppCell = ({ r }) => {
  if (r.inferredClientApp) {
    return <span className="sw-hint rl-inferred" title={`${storedAs(r)}. The app is inferred when the log is read and is not stored.`}>{inferredText(r)}</span>
  }
  const stored = appLabel(r)
  if (r.unidentified === 'anonymous') {
    return <span className="sw-hint" title={`${storedAs(r)}. No one was signed in and nothing names the app.`}>No app (anonymous)</span>
  }
  const label = stored || <span className="sw-hint">Unknown</span>
  if (r.unidentified === 'signed_in') {
    return (
      <span className="rl-app">
        {label}
        <Pill tone="bad" title="Someone was signed in, but nothing names the app this request came from">Unidentified</Pill>
      </span>
    )
  }
  return label
}

const FROM_DETAIL = {
  session: 'Inferred from this session: another request in it named the app.',
  // content read the session's own claim (what the app sent at sign-in or
  // on a refresh, or the app auth learned from an app-only route): no
  // request of the log to link to
  sessionClaim: 'Inferred from this session: its app named itself when it signed in or refreshed, or called an app-only route.',
  sessionRoute: 'Inferred from this session: another request in it was on an app-only route.',
  route: "Inferred from this request's route.",
  header: "From this request's own app header; its platform isn't one the log names."
}

const explanation = d => {
  if (d.inferredFrom === 'route') return d.inferredFromRequestID ? FROM_DETAIL.sessionRoute : FROM_DETAIL.route
  if (d.inferredFrom === 'session' && !d.inferredFromRequestID) return FROM_DETAIL.sessionClaim
  return FROM_DETAIL[d.inferredFrom] || 'Inferred when the log is read.'
}

// The inferred app in the detail drawer: the app, then where it came from
// (and the request it rests on), then that it isn't stored.
const InferredApp = ({ d }) => {
  const location = useLocation()
  const name = [inferredApp(d), d.inferredClientPlatform && `on ${d.inferredClientPlatform}`].filter(Boolean).join(' ')
  return (
    <span className="rl-traced">
      <span className="sw-hint rl-inferred">{name}</span>
      <span className="rl-trace-links">
        <span className="sw-hint">{explanation(d)}</span>
        {d.inferredFromRequestID &&
          <Link to={{ pathname: `/admin/request-log/${d.inferredFromRequestID}`, search: location.search }}>Open that request</Link>}
      </span>
      <span className="sw-hint small">Not stored: the App above is what the log recorded for this request.</span>
    </span>
  )
}

const UNIDENTIFIED_SIGNED_IN = "Someone was signed in, but this request's app sent no app header and nothing in its session or route names the app."
const UNIDENTIFIED_ANONYMOUS = 'No app (anonymous): no one was signed in and nothing names the app (often a scanner or a probe).'

// The detail drawer's rows (key, value) for the App and location section,
// after the stored App, Version and Platform. Empty for a named app.
export const originRows = d => {
  if (d.inferredClientApp) return [['Inferred app', <InferredApp d={d} />]]
  if (d.unidentified === 'signed_in') {
    return [['Origin', (
      <span className="rl-traced">
        <span><Pill tone="bad">Unidentified origin</Pill></span>
        <span className="small">{UNIDENTIFIED_SIGNED_IN}</span>
      </span>
    )]]
  }
  if (d.unidentified === 'anonymous') return [['Origin', <span className="sw-hint">{UNIDENTIFIED_ANONYMOUS}</span>]]
  return []
}
